import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = {
  general_support: 'hsl(var(--chart-1))',
  cbt: 'hsl(var(--chart-2))',
  psychotherapy: 'hsl(var(--chart-3))',
  timeline_integration: 'hsl(var(--chart-4))'
};

const MODE_LABELS = {
  general_support: 'General Support',
  cbt: 'CBT & Schema Therapy',
  psychotherapy: 'Psychotherapy',
  timeline_integration: 'Timeline Insights (QSP)'
};

export const TherapyModeAnalytics = () => {
  const { data: modeStats, isLoading } = useQuery({
    queryKey: ['therapy-mode-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_mode_analytics')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      // Aggregate totals by mode
      const totals = data.reduce((acc: any, row: any) => {
        if (!acc[row.mode]) {
          acc[row.mode] = {
            mode: row.mode,
            total_messages: 0,
            unique_users: new Set(),
            conversations_using_mode: new Set()
          };
        }
        acc[row.mode].total_messages += row.total_messages;
        acc[row.mode].unique_users.add(row.unique_users);
        acc[row.mode].conversations_using_mode.add(row.conversations_using_mode);
        return acc;
      }, {});

      return Object.values(totals).map((item: any) => ({
        mode: item.mode,
        total_messages: item.total_messages,
        unique_users: item.unique_users.size,
        conversations: item.conversations_using_mode.size,
        label: MODE_LABELS[item.mode as keyof typeof MODE_LABELS] || item.mode
      }));
    }
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading mode analytics...</div>;
  }

  if (!modeStats || modeStats.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Therapy Mode Usage Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No mode data available yet. Mode tracking will appear here as users engage with different therapeutic approaches.</p>
        </CardContent>
      </Card>
    );
  }

  const totalMessages = modeStats.reduce((sum, stat) => sum + stat.total_messages, 0);
  const pieData = modeStats.map(stat => ({
    name: stat.label,
    value: stat.total_messages,
    percentage: ((stat.total_messages / totalMessages) * 100).toFixed(1)
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Therapy Mode Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[modeStats[index].mode as keyof typeof COLORS]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Messages by Mode</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={modeStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total_messages" fill="hsl(var(--primary))" name="Total Messages" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Engagement by Mode</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={modeStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="unique_users" fill="hsl(var(--chart-2))" name="Unique Users" />
              <Bar dataKey="conversations" fill="hsl(var(--chart-3))" name="Conversations" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mode Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {modeStats.map((stat) => (
              <div key={stat.mode} className="border-b pb-4 last:border-0">
                <h3 className="font-semibold mb-2">{stat.label}</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Messages</p>
                    <p className="text-2xl font-bold">{stat.total_messages}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Unique Users</p>
                    <p className="text-2xl font-bold">{stat.unique_users}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Conversations</p>
                    <p className="text-2xl font-bold">{stat.conversations}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
